import { Box, IconButton, useMediaQuery, type Theme } from '@mui/material';
import { ArrowLeft, X } from 'lucide-react';
import { useCallback, useLayoutEffect, useRef, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Button } from './Button';
import { Icon } from './Icon';
import { visuallyHidden } from './visuallyHidden';

/**
 * Liste et detail cote a cote (maquette B1, principe n° 7).
 *
 * Ouvrir une fiche ne doit pas faire perdre la liste : ses filtres, sa page, sa
 * position de defilement. La liste **reste donc montee** a gauche, le detail
 * s'ouvre a droite (480 a 560 px, defilement propre), et la selection vit dans
 * l'URL (`/<base>/<id>`) : un rechargement ou un lien partage rouvrent la meme
 * fiche. Sous 1200 px, deux volets ne tiennent plus : le detail passe en plein
 * ecran avec un bouton retour, et la liste, masquee mais montee, retrouve sa
 * position a la fermeture.
 *
 * Accessibilite : a l'ouverture, le focus va au detail (region nommee) et le
 * changement est annonce ; Echap ferme le detail, sauf depuis un champ de saisie ;
 * a la fermeture, le focus
 * revient a la ligne qui l'avait ouvert — ou, apres un lien direct, a la ligne
 * qui etait marquee courante (`aria-current`, que pose `DataTable`).
 *
 * Emploi : une seule route `<base>/*` monte l'ecran, qui lit la selection par
 * `useDetailRoute(base)`. Deux routes distinctes pour la liste et la fiche
 * demonteraient la liste a chaque ouverture.
 */

/** Identifiant selectionne dans un chemin `<base>/<id>`, ou `null`. */
export const detailIdFromPath = (pathname: string, basePath: string): string | null => {
  if (!pathname.startsWith(`${basePath}/`)) {
    return null;
  }

  const segment = pathname.slice(basePath.length + 1).split('/')[0] ?? '';

  if (segment === '') {
    return null;
  }

  try {
    return decodeURIComponent(segment);
  } catch {
    // Un `%` isole n'identifie rien : la liste s'affiche seule.
    return null;
  }
};

export interface DetailRoute {
  readonly selectedId: string | null;
  readonly open: (id: string) => void;
  readonly close: () => void;
}

/**
 * Selection pilotee par l'URL. La chaine de requete est conservee dans les deux
 * sens : c'est elle qui porte les filtres d'une liste qui les met dans l'URL.
 */
export const useDetailRoute = (basePath: string): DetailRoute => {
  const location = useLocation();
  const navigate = useNavigate();
  const search = location.search;

  const open = useCallback(
    (id: string): void => {
      void navigate({ pathname: `${basePath}/${encodeURIComponent(id)}`, search });
    },
    [navigate, basePath, search],
  );

  const close = useCallback((): void => {
    void navigate({ pathname: basePath, search });
  }, [navigate, basePath, search]);

  return { selectedId: detailIdFromPath(location.pathname, basePath), open, close };
};

export interface MasterDetailProps {
  readonly list: ReactNode;
  readonly selectedId: string | null;
  readonly renderDetail: (id: string) => ReactNode;
  /** Nom du volet de detail (« Fiche du compte »), annonce a l'ouverture. */
  readonly detailLabel: string;
  readonly onClose: () => void;
}

/** Hauteur de la barre superieure de la coque, sous laquelle le detail se colle. */
const TOP_BAR_HEIGHT = 64;

export const MasterDetail = ({ list, selectedId, renderDetail, detailLabel, onClose }: MasterDetailProps): ReactElement => {
  const narrow = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const open = selectedId !== null;
  const listRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  /** Position de la liste avant un plein ecran ; `null` si rien n'a ete mis de cote. */
  const savedScroll = useRef<number | null>(null);
  /** Fiche deja traitee par l'effet : un simple redimensionnement ne rejoue rien. */
  const shownId = useRef<string | null>(null);

  // Avant peinture : le focus et le defilement bougent avant que l'oeil ne voie
  // la liste sauter. `narrow` est lu, mais un changement de largeur seul ne
  // deplace ni le focus ni le defilement : seul un changement de fiche le fait.
  useLayoutEffect(() => {
    if (selectedId === shownId.current) {
      return;
    }

    const previous = shownId.current;

    shownId.current = selectedId;

    if (selectedId !== null) {
      const active = document.activeElement;

      returnFocus.current =
        active instanceof HTMLElement && listRef.current?.contains(active) === true
          ? active
          : (listRef.current?.querySelector<HTMLElement>('[aria-current="true"]') ?? returnFocus.current);

      if (narrow && previous === null) {
        savedScroll.current = window.scrollY;
        window.scrollTo(0, 0);
      }

      detailRef.current?.focus({ preventScroll: true });

      return;
    }

    if (savedScroll.current !== null) {
      window.scrollTo(0, savedScroll.current);
      savedScroll.current = null;
    }

    const target = returnFocus.current;

    returnFocus.current = null;

    if (target?.isConnected === true) {
      target.focus({ preventScroll: true });
    } else {
      listRef.current?.focus({ preventScroll: true });
    }
  }, [selectedId, narrow]);

  const onDetailKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    // Une boite de dialogue ouverte dans le detail traite Echap elle-meme. Dans
    // un champ de saisie, Echap appartient au champ (vider, fermer une liste) :
    // fermer le volet y ferait perdre la saisie d'un formulaire de fiche.
    const inField =
      event.target instanceof HTMLElement &&
      event.target.closest('input, textarea, select, [contenteditable="true"], [role="combobox"]') !== null;

    if (event.key === 'Escape' && !event.defaultPrevented && !inField) {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: open && !narrow ? 'minmax(0, 1fr) clamp(480px, 36vw, 560px)' : 'minmax(0, 1fr)',
        gap: 3,
        alignItems: 'start',
      }}
    >
      <Box
        ref={listRef}
        tabIndex={-1}
        sx={{ minWidth: 0, outline: 'none', display: open && narrow ? 'none' : 'block' }}
      >
        {list}
      </Box>

      {/* Toujours rendue : une region vivante creee avec son texte n'est pas lue. */}
      <Box role="status" sx={visuallyHidden}>
        {open ? `Volet ouvert : ${detailLabel}` : ''}
      </Box>

      {selectedId === null ? null : (
        <Box
          component="section"
          ref={detailRef}
          tabIndex={-1}
          aria-label={detailLabel}
          onKeyDown={onDetailKeyDown}
          sx={[
            { minWidth: 0, outline: 'none', '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: 2 } },
            narrow
              ? {}
              : {
                  position: 'sticky',
                  top: TOP_BAR_HEIGHT + 16,
                  maxHeight: `calc(100vh - ${String(TOP_BAR_HEIGHT + 32)}px)`,
                  overflowY: 'auto',
                  bgcolor: 'tokens.surface',
                  border: 1,
                  borderColor: 'tokens.border',
                  borderRadius: '16px',
                  p: 2.5,
                },
          ]}
        >
          <Box sx={{ display: 'flex', justifyContent: narrow ? 'flex-start' : 'flex-end', mb: 1 }}>
            {narrow ? (
              <Button variant="ghost" icon={ArrowLeft} onClick={onClose}>
                Retour à la liste
              </Button>
            ) : (
              <IconButton aria-label="Fermer le détail" onClick={onClose} sx={{ width: 40, height: 40, color: 'tokens.textMuted' }}>
                <Icon icon={X} size={18} />
              </IconButton>
            )}
          </Box>
          {renderDetail(selectedId)}
        </Box>
      )}
    </Box>
  );
};
